/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import TaxAppealGuidance from './TaxAppealGuidance';
import PropertyDisplay from './PropertyDisplay';
import AddressAutocomplete from './AddressAutocomplete';
import ComparableSales, { ComparableSearchCriteria } from './ComparableSales';
import CountyVerification from './CountyVerification';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import getApiBaseUrl from '@/utils/getBaseUrl';
import NoSoldPriceModal from './NoSoldPriceModal';

interface PropertyFormProps {
  onPropertyDataLoaded?: (isLoaded: boolean) => void;
}

// Updated function to fetch property data by ID and handle the new response format
const fetchPropertyData = async (address: string, propertyId?: string) => {
  // If we have a property ID, use that to fetch the specific property
  if (propertyId) {
    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/properties/${propertyId}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch property data');
      }
      const data = await response.json();

      // Map the API response to our PropertyRecord format
      if (data && data.data) {
        return { ...transformApiResponse(data.data), mls: data.mls };
      }
      throw new Error('Invalid data structure received from API');
    } catch (error) {
      console.error('Error fetching property by ID:', error);
      throw error;
    }
  }

  // Otherwise, use fallback data (this would be replaced with a real API call in production)
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        address: 'LOT 15 BLK 4 ROCK CREEK RANCH FLG 15',
        subdivision: 'Rock Creek Ranch Flg 15 - Su',
        county: 'Boulder County',
        parcelAccountNumber: 'R0116128',
        apn: '1575-30-3-07-015',
        assessmentYear: 2024,
        marketValue: 839100,
        assessedValue: 52535,
        assessedLandValue: 16610,
        assessedImprovementValue: 35925,
        marketLandValue: 302900,
        marketImprovementValue: 536200,
        taxAmount: '5443.68',

        livingSquareFeet: 1648,
        buildingSquareFeet: 2257,
        lotAcres: 0.17,
        lotSquareFeet: 7449,
        bedrooms: 5,
        bathrooms: 2,
        partialBathrooms: 2,
        stories: 2,
        roomsCount: 7,
        parkingSpaces: 2,
        garageType: 'Attached Garage',
        garageSquareFeet: 609,
        basementType: 'Finished',
        basementSquareFeet: 656,
        basementSquareFeetFinished: 656,

        construction: 'Frame',
        roofMaterial: 'Composition Shingle',
        interiorStructure: 'Drywall',
        airConditioningType: 'Central',
        heatingType: 'Forced Air Unit',
        heatingFuelType: 'Gas',
        fireplace: false,
        plumbingFixturesCount: 12,

        hoa: true,
        pool: false,
        deck: false,
        patio: false,
        porchType: null,
        porchArea: null,
        rvParking: false,
        attic: true,
        breezeway: false,
        carport: false,
        safetyFireSprinklers: false,

        legalDescription: 'LOT 15 BLK 4 ROCK CREEK RANCH FLG 15',
        lotNumber: '15',
        propertyUse: 'Single Family Residence',
        propertyUseCode: 385,
        unitsCount: 1,
        buildingsCount: 1,
        taxExemptionHomeownerFlag: false,
        taxDelinquentYear: null,

        latitude: 39.9322919,
        longitude: -105.1624854,
        censusBlock: '1007',
        censusBlockGroup: '7',
        censusTract: '061300',
        floodZoneDescription: 'AREA OF MINIMAL FLOOD HAZARD',

        lastSaleDate: '2024-04-18',
        pricePerSquareFoot: 0,
        estimatedValue: null,

        // Legacy fields for compatibility
        currentAssessment: 52535,
        fairMarketValue: 839100,
        accountNumber: 'R0116128',
      });
    }, 1000);
  });
};

// Transform API response to match our PropertyRecord format
const transformApiResponse = (apiData: any): any => {
  return {
    address:
      apiData.propertyInfo?.address?.label ||
      apiData.propertyInfo?.address?.address ||
      '',
    subdivision: apiData.lotInfo?.subdivision || '',
    county: apiData.propertyInfo?.address?.county || '',
    state: apiData.propertyInfo?.address?.state || '',
    parcelAccountNumber:
      apiData.propertyInfo?.parcelAccountNumber ||
      apiData.taxInfo?.propertyId?.toString() ||
      '',
    apn: apiData.lotInfo?.apn || '',
    assessmentYear: apiData.taxInfo?.assessmentYear || new Date().getFullYear(),
    marketValue: apiData.taxInfo?.marketValue || 0,
    assessedValue: apiData.taxInfo?.assessedValue || 0,
    assessedLandValue: apiData.taxInfo?.assessedLandValue || 0,
    assessedImprovementValue: apiData.taxInfo?.assessedImprovementValue || 0,
    marketLandValue: apiData.taxInfo?.marketLandValue || 0,
    marketImprovementValue: apiData.taxInfo?.marketImprovementValue || 0,
    taxAmount: apiData.taxInfo?.taxAmount || '0',

    livingSquareFeet: apiData.propertyInfo?.livingSquareFeet || 0,
    buildingSquareFeet: apiData.propertyInfo?.buildingSquareFeet || 0,
    lotAcres: apiData.lotInfo?.lotAcres || 0,
    lotSquareFeet: apiData.lotInfo?.lotSquareFeet || 0,
    bedrooms: apiData.propertyInfo?.bedrooms || 0,
    bathrooms: apiData.propertyInfo?.bathrooms || 0,
    partialBathrooms: apiData.propertyInfo?.partialBathrooms || 0,
    stories: apiData.propertyInfo?.stories || 0,
    roomsCount: apiData.propertyInfo?.roomsCount || 0,
    parkingSpaces: apiData.propertyInfo?.parkingSpaces || 0,
    garageType: apiData.propertyInfo?.garageType || '',
    garageSquareFeet: apiData.propertyInfo?.garageSquareFeet || 0,
    basementType: apiData.propertyInfo?.basementType || '',
    basementSquareFeet: apiData.propertyInfo?.basementSquareFeet || 0,
    basementSquareFeetFinished:
      apiData.propertyInfo?.basementSquareFeetFinished || 0,

    construction: apiData.propertyInfo?.construction || '',
    roofMaterial: apiData.propertyInfo?.roofMaterial || '',
    interiorStructure: apiData.propertyInfo?.interiorStructure || '',
    airConditioningType: apiData.propertyInfo?.airConditioningType || '',
    heatingType: apiData.propertyInfo?.heatingType || '',
    heatingFuelType: apiData.propertyInfo?.heatingFuelType || '',
    fireplace: apiData.propertyInfo?.fireplace || false,
    plumbingFixturesCount: apiData.propertyInfo?.plumbingFixturesCount || 0,

    hoa: apiData.propertyInfo?.hoa || false,
    pool: apiData.propertyInfo?.pool || false,
    deck: apiData.propertyInfo?.deck || false,
    patio: apiData.propertyInfo?.patio || false,
    porchType: apiData.propertyInfo?.porchType || null,
    porchArea: apiData.propertyInfo?.porchArea || null,
    rvParking: apiData.propertyInfo?.rvParking || false,
    attic: apiData.propertyInfo?.attic || false,
    breezeway: apiData.propertyInfo?.breezeway || false,
    carport: apiData.propertyInfo?.carport || false,
    safetyFireSprinklers: apiData.propertyInfo?.safetyFireSprinklers || false,

    legalDescription: apiData.lotInfo?.legalDescription || '',
    lotNumber: apiData.lotInfo?.lotNumber || '',
    propertyUse:
      apiData.propertyInfo?.propertyUse || apiData.lotInfo?.propertyUse || '',
    propertyUseCode: apiData.propertyInfo?.propertyUseCode || 0,
    unitsCount: apiData.propertyInfo?.unitsCount || 1,
    buildingsCount: apiData.propertyInfo?.buildingsCount || 1,
    taxExemptionHomeownerFlag:
      apiData.propertyInfo?.taxExemptionHomeownerFlag || false,
    taxDelinquentYear: apiData.taxInfo?.taxDelinquentYear || null,

    latitude: apiData.propertyInfo?.latitude || 0,
    longitude: apiData.propertyInfo?.longitude || 0,
    censusBlock: apiData.lotInfo?.censusBlock || '',
    censusBlockGroup: apiData.lotInfo?.censusBlockGroup || '',
    censusTract: apiData.lotInfo?.censusTract || '',
    floodZoneDescription: apiData.floodZoneDescription || '',

    lastSaleDate: apiData.lastSale?.saleDate || '',
    pricePerSquareFoot: apiData.propertyInfo?.pricePerSquareFoot || 0,
    estimatedValue: apiData.estimatedValue || null,

    // Legacy fields for compatibility
    currentAssessment: apiData.taxInfo?.assessedValue || 0,
    fairMarketValue: apiData.taxInfo?.marketValue || 0,
    accountNumber: apiData.taxInfo?.propertyId?.toString() || '',
  };
};

const PropertyForm: React.FC = () => {
  const { isAuthenticated } = useAuth0();
  const [query] = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [propertyData, setPropertyData] = useState(null);
  const [comparableSearchCriteria, setComparableSearchCriteria] =
    useState<ComparableSearchCriteria | null>(null);
  const [countyVerified, setCountyVerified] = useState(false);
  const [verifiedDateRange, setVerifiedDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);
  const [comparableData, setComparableData] = useState<any[]>([]);
  const [showNoSoldPriceModal, setShowNoSoldPriceModal] = useState(false);

  const helpRequestId = query.get('helpPropertyId');
  const helpPropertyAddress = query.get('helpPropertyAddress');

  const handleSearch = async (address: string, propertyId?: string) => {
    if (!address) {
      toast({
        title: 'Error',
        description: 'Please enter a valid property address',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      // Clear all previous data immediately when starting a new search
      setPropertyData(null);
      setComparableSearchCriteria(null);
      setCountyVerified(false);
      setVerifiedDateRange(null);
      setComparableData([]);

      const data = await fetchPropertyData(address, propertyId);
      console.log({ data });

      if (
        [
          'AK',
          'ID',
          'KS',
          'LA',
          'MS',
          'MO',
          'MT',
          'NM',
          'ND',
          'TX',
          'UT',
          'WY',
        ].includes(data?.state)
      ) {
        setShowNoSoldPriceModal(true);
      }

      setPropertyData(data);

      // Extract and save comparable search criteria from the raw API response
      if (propertyId) {
        try {
          const response = await fetch(
            `${getApiBaseUrl()}/api/v1/properties/${propertyId}`,
          );
          if (response.ok) {
            const apiData = await response.json();
            if (apiData && apiData.data) {
              const criteria: ComparableSearchCriteria = {
                livingSquareFeet:
                  apiData.data.propertyInfo?.livingSquareFeet || 0,
                yearBuilt: apiData.data.propertyInfo?.yearBuilt || 0,
                propertyType: apiData.data.propertyType || '',
                propertyUseCode:
                  apiData.data.propertyInfo.propertyUseCode || '',
                longitude: apiData.data.propertyInfo?.longitude || 0,
                latitude: apiData.data.propertyInfo?.latitude || 0,
                county: apiData.data.propertyInfo?.address?.county || '',
                state: apiData.data.propertyInfo?.address?.state || '',
              };
              setComparableSearchCriteria(criteria);
              // Store criteria in localStorage for CountyVerification to access
              window.localStorage.setItem(
                'comparableSearchCriteria',
                JSON.stringify(criteria),
              );
              window.localStorage.setItem(
                'propertyInfo',
                JSON.stringify({
                  ...apiData.data.propertyInfo,
                  legalDescription: apiData.data.lotInfo?.legalDescription,
                  mls: data.mls,
                  marketValue: data.marketValue,
                }),
              );
            }
          }
        } catch (error) {
          console.error('Error extracting comparable search criteria:', error);
        }
      }

      toast({
        title: 'Property found!',
        description: "We've loaded your property information.",
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Unable to fetch property data. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCountyVerified = (
    dateRange?: { startDate: Date; endDate: Date },
    comparableDataFromApi?: any[],
  ) => {
    setCountyVerified(true);
    if (dateRange) {
      setVerifiedDateRange(dateRange);
    }
    if (comparableDataFromApi) {
      setComparableData(comparableDataFromApi);
    }
  };

  useEffect(() => {
    if (helpRequestId && helpPropertyAddress && isAuthenticated) {
      handleSearch(helpPropertyAddress, helpRequestId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, helpRequestId, helpPropertyAddress]);

  return (
    <div id="property-form" className="w-full max-w-3xl mx-auto">
      <AddressAutocomplete
        onSearch={handleSearch}
        isLoading={loading}
        inputDefaultValue={helpPropertyAddress}
      />
      <NoSoldPriceModal
        isOpen={showNoSoldPriceModal}
        setIsOpen={() => setShowNoSoldPriceModal(false)}
      />

      {propertyData && (
        <div className="mt-6 animate-fade-in space-y-6">
          {/* Property display wrapper with visual distinction */}
          <div className="relative bg-gradient-to-b from-tax-softWhite to-tax-lightGray rounded-xl p-6 border border-tax-borderGray shadow-lg">
            {/* Your Property Label - Updated with purple colors */}
            <div className="absolute -top-4 left-8 bg-[#1976D2] text-white px-4 py-1 rounded-full font-medium flex items-center shadow-sm border">
              <span>Your Property</span>
              <svg
                className="w-12 h-12 ml-1"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 10C12 10 12 15 7 15"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 15L7 15L7 12"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Enhanced PropertyDisplay component */}
            <PropertyDisplay propertyData={propertyData} />
          </div>

          {/* Tax Appeal Guidance - Only shown after property data is loaded */}
          <div className="mt-8">
            {/* Tax Appeal Guidance component */}
            <TaxAppealGuidance county={propertyData.county} />
          </div>

          {/* County Verification Section - Always show when property data is loaded */}
          <CountyVerification
            county={propertyData.county}
            state={propertyData.state}
            onVerified={handleCountyVerified}
          />

          {/* ComparableSales component - Only show after county verification */}
          {countyVerified && (
            <ComparableSales
              propertyDataLoaded={!!propertyData}
              comparableSearchCriteria={comparableSearchCriteria}
              subjectPropertySqft={
                comparableSearchCriteria?.livingSquareFeet || 0
              }
              dateRange={verifiedDateRange}
              initialComparableData={comparableData}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyForm;
